import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const UNITS = ["scatole", "pezzi", "bobine", "sacchi", "kg"];

const empty = {
  name: "",
  code: "",
  quantity: 0,
  unit: "scatole",
  min_threshold: 5,
};

export default function ProductForm({
  open,
  onOpenChange,
  initial,
  onSubmit,
}) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (initial) {
      setForm({ ...empty, ...initial });
    } else {
      setForm(empty);
    }
  }, [initial, open]);

  const handleChange = (key) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.code?.trim()) {
      toast.error("Nome e codice sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        quantity: Number(form.quantity) || 0,
        unit: form.unit,
        min_threshold: Number(form.min_threshold) || 0,
      };
      await onSubmit(payload);
      toast.success(
        isEdit ? "Prodotto aggiornato" : "Prodotto creato con successo",
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="product-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Modifica Prodotto" : "Nuovo Prodotto"}
          </DialogTitle>
          <DialogDescription>
            Inserisci i dati della rete da confezionamento. La giacenza sarà
            aggiornata tramite i movimenti di entrata/uscita.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="p-name">Nome prodotto *</Label>
              <Input
                id="p-name"
                placeholder="Es. Rete tubolare rossa 500mm"
                value={form.name}
                onChange={handleChange("name")}
                data-testid="product-name-input"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-code">Codice *</Label>
              <Input
                id="p-code"
                placeholder="RT-500-R"
                value={form.code}
                onChange={handleChange("code")}
                data-testid="product-code-input"
                className="mono"
                required
                disabled={isEdit}
              />
              {isEdit && (
                <p className="text-xs text-slate-500">
                  Il codice non è modificabile.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-unit">Unità</Label>
              <Select
                value={form.unit}
                onValueChange={(v) => setForm((f) => ({ ...f, unit: v }))}
              >
                <SelectTrigger id="p-unit" data-testid="product-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isEdit && (
              <div className="space-y-1.5">
                <Label htmlFor="p-qty">Giacenza iniziale</Label>
                <Input
                  id="p-qty"
                  type="number"
                  min="0"
                  step="1"
                  value={form.quantity}
                  onChange={handleChange("quantity")}
                  data-testid="product-quantity-input"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="p-threshold">Soglia minima</Label>
              <Input
                id="p-threshold"
                type="number"
                min="0"
                step="1"
                value={form.min_threshold}
                onChange={handleChange("min_threshold")}
                data-testid="product-threshold-input"
              />
              <p className="text-xs text-slate-500">
                Sotto questa quantità riceverai la notifica.
              </p>
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="product-form-cancel"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="product-form-submit"
            >
              {saving
                ? "Salvataggio..."
                : isEdit
                  ? "Aggiorna"
                  : "Crea prodotto"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
